import { useApp } from '@/contexts';

import { useState } from 'react';

import { Edit as EditIcon, PlayArrow as PlayIcon } from '@mui/icons-material';
import { Box, Button, Tab, Tabs, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router';

import { PageLoader } from '@/components/common';
import { PageContainer, PageHeading } from '@/components/layout';
import { logger } from '@/utils/logger';

import { useGetWizardQuery } from '../api/wizardsApi';

/**
 * Wizard Config Page - View and manage wizard configuration
 * Based on dataProtect wizardConfig.js
 */
export const WizardConfigPage = () => {
  const { appId } = useApp();
  const { transId } = useParams<{ transId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  const {
    data: wizard,
    isLoading,
    error,
  } = useGetWizardQuery({
    appId: appId!,
    wizardId: transId!,
  });

  const handleEdit = () => {
    navigate(`/a/${appId}/wiz/${transId}/configure`);
  };

  const handleRun = () => {
    // TODO: Implement wizard execution
    logger.warn('Run wizard:', transId);
  };

  if (isLoading) {
    return (
      <PageContainer>
        <PageLoader />
      </PageContainer>
    );
  }

  if (error || !wizard) {
    return (
      <PageContainer>
        <Typography color="error">Failed to load wizard configuration</Typography>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeading
        title={wizard.name}
        subtitle={`${wizard.sourceLabel} → ${wizard.destinationLabel}`}
        action={
          <>
            <Button variant="outlined" startIcon={<EditIcon />} onClick={handleEdit}>
              Edit Configuration
            </Button>
            <Button variant="contained" startIcon={<PlayIcon />} onClick={handleRun} sx={{ ml: 1 }}>
              Run Integration
            </Button>
          </>
        }
      />

      <Tabs value={activeTab} onChange={(_e, newValue) => setActiveTab(newValue)}>
        <Tab label="Configuration" />
        <Tab label="Execution Log" />
        <Tab label="Recipe View" />
      </Tabs>

      <Box sx={{ mt: 3 }}>
        {activeTab === 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Wizard Configuration
            </Typography>
            <pre>{JSON.stringify(wizard.versions[0]?.config, null, 2)}</pre>
          </Box>
        )}

        {activeTab === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Execution Log
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No executions yet
            </Typography>
          </Box>
        )}

        {activeTab === 2 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Generated Recipe
            </Typography>
            <pre>{wizard.versions[0]?.recipe || 'No recipe generated yet'}</pre>
          </Box>
        )}
      </Box>
    </PageContainer>
  );
};
